import React, { memo, useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSound } from '../contexts/SoundContext';

const SkinCard = memo(({ 
  skin, 
  index, 
  viewMode, 
  imagePath, 
  onOpenModal, 
  onImageError,
  isLiked = false,
  likeCount = 0,
  onToggleLike,
  isLiking = false
}) => {
  const { t } = useLanguage();
  const { playButtonClick } = useSound();
  const [isInView, setIsInView] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  
  const skinName = skin['Skin name'] || '';
  const mechName = skin['Mech name'] || '';

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.01
      }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  if (viewMode === 'grid') {
    return (
      <div
        ref={containerRef}
        className="bg-white dark:bg-gray-700 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-[#FF8A65] overflow-hidden group"
      >
        {/* Image */}
        <div 
          className="aspect-square bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden cursor-pointer relative"
          onClick={() => onOpenModal(skin, index)}
        >
          {/* Skeleton placeholder */}
          {!imageLoaded && (
            <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          {isInView && (
            <img
              ref={imgRef}
              src={imagePath}
              alt={skinName}
              className={`w-full h-full object-contain p-2 group-hover:scale-105 transition-all duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={handleImageLoad}
              onError={onImageError}
            />
          )}
        </div>
        
        {/* Info */}
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 line-clamp-2">
            {skinName}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            <span className="font-medium">{t('legendarySkins.mechName')}:</span> {mechName}
          </p>
          {/* Like Button */}
          {onToggleLike && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleLike(skinName);
                playButtonClick();
              }}
              disabled={isLiking}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-200 ${
                isLiked
                  ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                  : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-500'
              } ${isLiking ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              aria-label={isLiked ? t('legendarySkins.unlike') : t('legendarySkins.like')}
            >
              <svg
                className={`w-5 h-5 transition-transform duration-200 ${isLiking ? 'animate-pulse' : ''}`}
                fill={isLiked ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span className="text-sm font-medium">
                {likeCount > 0 ? likeCount : ''}
              </span>
            </button>
          )}
        </div>
      </div>
    );
  }

  // List view
  return (
    <div
      ref={containerRef}
      className="bg-white dark:bg-gray-700 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-[#FF8A65] p-4 flex items-center gap-4 group"
    >
      {/* Thumbnail */}
      <div 
        className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer relative"
        onClick={() => onOpenModal(skin, index)}
      >
        {/* Skeleton placeholder */}
        {!imageLoaded && (
          <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        {isInView && (
          <img
            ref={imgRef}
            src={imagePath}
            alt={skinName}
            className={`w-full h-full object-contain p-1 group-hover:scale-105 transition-all duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={handleImageLoad}
            onError={onImageError}
          />
        )}
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-1 truncate">
          {skinName}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <span className="font-medium">{t('legendarySkins.mechName')}:</span> {mechName}
        </p>
        {/* Like Button */}
        {onToggleLike && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleLike(skinName);
              playButtonClick();
            }}
            disabled={isLiking}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-200 ${
              isLiked
                ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-500'
            } ${isLiking ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            aria-label={isLiked ? t('legendarySkins.unlike') : t('legendarySkins.like')}
          >
            <svg
              className={`w-5 h-5 transition-transform duration-200 ${isLiking ? 'animate-pulse' : ''}`}
              fill={isLiked ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <span className="text-sm font-medium">
              {likeCount > 0 ? likeCount : ''}
            </span>
          </button>
        )}
      </div>
    </div>
  );
});

SkinCard.displayName = 'SkinCard';

export default SkinCard;

